import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

import { Button, IconButton } from './Button';
import { Icon } from './Icon';

// --------------------------------------------------------------------------- //
// Surfaces
// --------------------------------------------------------------------------- //
export function Card({ children, className, as: Component = 'div' }) {
  return (
    <Component
      className={cn(
        'border-line bg-surface rounded-card shadow-card min-w-0 border p-4',
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function CardHeader({ title, description, action }) {
  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
      <div className="min-w-0">
        <h2 className="text-ink text-base font-semibold">{title}</h2>
        {description && <p className="text-ink-muted mt-0.5 text-[12px]">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * The top of every page: one title, one line of context, the page's actions.
 *
 * `action` may be a single button or several; they wrap under the title on a
 * narrow screen instead of squeezing it. `eyebrow` is a small label above the
 * title for a page that sits inside a larger area ("Reports", "Billing").
 */
export function PageHeader({ title, description, action, eyebrow }) {
  return (
    <header className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
      <div className="min-w-0 max-w-3xl">
        {eyebrow && (
          <p className="text-primary mb-0.5 text-xs font-semibold tracking-wide uppercase">
            {eyebrow}
          </p>
        )}
        <h1 className="text-ink text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="text-ink-muted mt-0.5 text-sm">{description}</p>}
      </div>
      {action && (
        <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">{action}</div>
      )}
    </header>
  );
}

// --------------------------------------------------------------------------- //
// Badges and meters
// --------------------------------------------------------------------------- //

/**
 * Tones carry meaning, never decoration -- see the status block in
 * `index.css`. `brand` and `primary` are the same tone under two names, so
 * older call sites keep working.
 */
const BADGE_TONES = {
  neutral: 'bg-surface-muted text-ink-muted ring-line',
  brand: 'bg-brand-50 text-brand-700 ring-brand-100',
  primary: 'bg-brand-50 text-brand-700 ring-brand-100',
  info: 'bg-info-50 text-info-700 ring-info-100',
  success: 'bg-success-50 text-success-700 ring-success-100',
  warning: 'bg-warning-50 text-warning-800 ring-warning-100',
  danger: 'bg-danger-50 text-danger-700 ring-danger-100',
};

const BADGE_DOTS = {
  neutral: 'bg-ink-muted/60',
  brand: 'bg-brand-500',
  primary: 'bg-brand-500',
  info: 'bg-info-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
};

export function Badge({ children, tone = 'neutral', dot = false, className, title }) {
  const known = BADGE_TONES[tone] ? tone : 'neutral';
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-px text-xs font-medium whitespace-nowrap',
        'whitespace-nowrap ring-1 ring-inset',
        BADGE_TONES[known],
        className,
      )}
    >
      {dot && (
        <span aria-hidden="true" className={cn('size-1.5 rounded-full', BADGE_DOTS[known])} />
      )}
      {children}
    </span>
  );
}

export function ProgressBar({ value, label, className }) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className={className}>
      {label && (
        <div className="mb-1.5 flex items-center justify-between text-sm">
          <span className="text-ink-muted">{label}</span>
          <span className="text-ink font-medium">{clamped}%</span>
        </div>
      )}
      <div
        className="bg-surface-muted h-2 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="bg-brand-500 h-full rounded-full transition-[width]"
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

export function StatTile({ label, value, hint }) {
  return (
    <div className="border-line bg-surface rounded-card shadow-card border px-3.5 py-3">
      <p className="text-ink-muted text-caption font-medium tracking-wide uppercase">{label}</p>
      <p className="text-ink mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="text-ink-muted mt-0.5 text-xs">{hint}</p>}
    </div>
  );
}

const METRIC_TONES = {
  brand: 'bg-brand-50 text-brand-700',
  primary: 'bg-brand-50 text-brand-700',
  neutral: 'bg-surface-muted text-ink-muted',
  info: 'bg-info-50 text-info-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-800',
  danger: 'bg-danger-50 text-danger-700',
};

/**
 * A headline number with an icon and one line of context.
 *
 * Deliberately a separate component from `StatTile` rather than more props on
 * it: the tile is the dense four-across form used inside reports, this is the
 * top-of-dashboard form that has room to explain itself. Passing `to` turns
 * the whole card into a link, because a number an admin is meant to act on
 * should not make them hunt for the verb.
 */
/**
 * A number with its label. `to` makes it a link; `onClick` makes it a button
 * (e.g. a stat that also filters the list below), with `active` marking the
 * one currently applied.
 */
export function MetricCard({
  label,
  value,
  hint,
  icon,
  tone = 'brand',
  to,
  onClick,
  active = false,
  className,
}) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-ink-muted text-caption font-medium tracking-wide uppercase">{label}</p>
        {icon && (
          <span
            className={cn('grid size-8 shrink-0 place-items-center rounded-lg', METRIC_TONES[tone])}
          >
            <Icon name={icon} size={16} />
          </span>
        )}
      </div>
      <p className="text-ink mt-0.5 text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
      {hint && <p className="text-ink-muted mt-1 text-xs">{hint}</p>}
    </>
  );

  const shell = cn(
    'border-line bg-surface rounded-card shadow-card border p-4',
    active && 'border-brand-300 ring-brand-100 ring-2',
    className,
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
          shell,
          'hover:border-brand-200 w-full text-left transition duration-200 hover:shadow-md',
        )}
      >
        {body}
      </button>
    );
  }

  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          shell,
          'hover:border-brand-200 block transition duration-200 hover:shadow-md',
        )}
      >
        {body}
      </Link>
    );
  }
  return <div className={shell}>{body}</div>;
}

// --------------------------------------------------------------------------- //
// Modal
// --------------------------------------------------------------------------- //

const MODAL_SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
};

/** Everything a keyboard can land on, for keeping Tab inside the dialog. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, title, description, onClose, children, footer, size = 'md' }) {
  const dialogRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  // Focus moves to the dialog when it opens, and only then. `onClose` is
  // almost always an inline arrow, so it has a new identity on every render --
  // combining these two effects meant every keystroke inside the dialog stole
  // focus back from whatever field was being typed into, and only the first
  // character ever landed.
  useEffect(() => {
    if (!open) return undefined;
    // Whatever opened the dialog gets focus back when it closes, so a
    // keyboard user is not dropped at the top of the page.
    const opener = document.activeElement;
    dialogRef.current?.focus();
    // The page behind should not scroll under an open dialog.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      // Tab and Shift+Tab cycle within the dialog rather than escaping to the
      // page behind it, which a sighted mouse user never sees but a keyboard
      // user would be stranded in.
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  // Drawn on `document.body`, so a dialog opened from a row's "More" menu (or
  // from any clipped or hidden container) still shows over the whole page.
  return createPortal(
    // A sheet from the bottom on a phone, a centred dialog from `sm` up.
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="bg-night-deep/50 absolute inset-0 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'bg-surface relative z-10 flex w-full flex-col shadow-xl focus:outline-none',
          'rounded-t-panel sm:rounded-panel',
          // A form taller than the viewport has to scroll inside the dialog;
          // without this the submit button is simply unreachable.
          'max-h-[92vh] sm:max-h-[90vh]',
          MODAL_SIZES[size] ?? MODAL_SIZES.md,
        )}
      >
        <div className="border-line flex items-start justify-between gap-4 border-b px-5 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-ink text-lg font-semibold">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="text-ink-muted mt-0.5 text-[12px]">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-muted hover:bg-surface-muted hover:text-ink -mt-0.5 -mr-2 grid size-7 shrink-0 place-items-center rounded-md transition"
          >
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="border-line bg-surface-muted/40 rounded-b-panel flex flex-col-reverse gap-2 border-t px-5 py-3 sm:flex-row sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  tone = 'danger',
  isLoading = false,
  onConfirm,
  onCancel,
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-ink-muted text-sm">{message}</p>
    </Modal>
  );
}

// --------------------------------------------------------------------------- //
// Pagination
// --------------------------------------------------------------------------- //
/**
 * "Showing 1–20 of 335" on the left, "‹ Page 1 of 17 ›" on the right.
 *
 * `pageSize` turns the summary into a range and keeps it visible on a single
 * page, as a list's footer; without it the component behaves as it always
 * did and disappears when there is only one page.
 */
export function Pagination({ page, totalPages, totalItems, onPageChange, pageSize, className }) {
  if (totalPages <= 1 && !pageSize) return null;

  const from = pageSize ? Math.min((page - 1) * pageSize + 1, totalItems) : null;
  const to = pageSize ? Math.min(page * pageSize, totalItems) : null;
  const noun = totalItems === 1 ? 'result' : 'results';

  return (
    <nav
      // `mt-4` sits it under a table inside a plain Card, as before; a list
      // card's footer passes className="" because the footer spaces itself.
      className={cn('flex flex-wrap items-center justify-between gap-3', className ?? 'mt-4')}
      aria-label="Pagination"
    >
      <p className="text-ink-muted text-xs sm:text-sm">
        {pageSize ? (
          <>
            Showing <span className="text-ink font-medium tabular-nums">{from}</span>–
            <span className="text-ink font-medium tabular-nums">{to}</span> of{' '}
            <span className="text-ink font-medium tabular-nums">{totalItems}</span>
          </>
        ) : (
          <>
            {totalItems} {noun}
          </>
        )}
      </p>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <IconButton
            icon="chevron"
            label="Previous page"
            variant="secondary"
            className="[&_svg]:rotate-180"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          />
          <span className="text-ink-muted px-2 text-xs tabular-nums sm:text-sm">
            Page <span className="text-ink font-medium">{page}</span> of {totalPages}
          </span>
          <IconButton
            icon="chevron"
            label="Next page"
            variant="secondary"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          />
        </div>
      )}
    </nav>
  );
}
